import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, Plus, File, Send, Printer, RefreshCw, Eye, Pencil, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { DocumentEditor } from './DocumentEditor';
import { SafeContent } from '@/components/common/SafeContent';
import { clinicalDocumentLabel, type ClinicalDocumentUiType } from '@/lib/clinicalDocuments';
import {
  confirmMemedDocument,
  downloadDocumentPdfFile,
  getDocumentsByAppointment,
  getDocumentAccess,
  getDocumentById,
  getDocumentRenewalBase,
  sendDocumentEmail,
  type DocumentDTO,
  type PrescriptionRenewalBase,
} from '@/services/documents';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useMemedPrescription } from '@/hooks/useMemedPrescription';
import type { MemedPrescriptionResult } from '@/integrations/memed';
import { memedKindFromClinicalUi, type MemedDocumentKind } from '@/integrations/memed/memed-document-kind';
import { documentStatusLabel } from '@/lib/documentStatus';
import { isMemedPrescriptionRenewable } from '@/lib/prescriptionRenewal';

interface PatientDocumentsTabProps {
  appointmentId: string;
  patientId: string;
  onDocumentGenerated?: (fileName: string, type: string, documentId?: string) => void;
  /** Ao montar, inicia renovação Memed deste documento (deep-link da lista de receitas). */
  autoRenewDocumentId?: string | null;
}

const MEMED_PRESCRIPTION_TYPES: ClinicalDocumentUiType[] = ['PRESCRIPTION', 'RECIPE'];
const MEMED_CERTIFICATE_EXAM_TYPES: ClinicalDocumentUiType[] = ['CERTIFICATE', 'EXAM'];

type PersistState = 'idle' | 'saving' | 'success' | 'error';

export const PatientDocumentsTab: React.FC<PatientDocumentsTabProps> = ({
  appointmentId,
  patientId,
  onDocumentGenerated,
  autoRenewDocumentId = null,
}) => {
  const [documents, setDocuments] = useState<DocumentDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorType, setEditorType] = useState<ClinicalDocumentUiType>('PRESCRIPTION');
  const [editingDocument, setEditingDocument] = useState<DocumentDTO | null>(null);
  const [viewingDocument, setViewingDocument] = useState<DocumentDTO | null>(null);
  const [viewingAccessUrl, setViewingAccessUrl] = useState<string | null>(null);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [persistState, setPersistState] = useState<PersistState>('idle');
  const [persistError, setPersistError] = useState<string | null>(null);
  const [renewLoadingId, setRenewLoadingId] = useState<string | null>(null);
  const [renewalBanner, setRenewalBanner] = useState<string | null>(null);
  const [renewalItemsPreview, setRenewalItemsPreview] = useState<string[]>([]);
  const [showMemedImport, setShowMemedImport] = useState(false);
  const [memedImportId, setMemedImportId] = useState('');
  const pendingLegacyTypeRef = useRef<ClinicalDocumentUiType>('PRESCRIPTION');
  const pendingKindRef = useRef<MemedDocumentKind>('prescription');
  const pendingPersistRef = useRef<MemedPrescriptionResult | null>(null);
  const renewalSourceDocumentIdRef = useRef<string | null>(null);
  const persistInFlightRef = useRef(false);
  const autoRenewDoneRef = useRef(false);

  const openLegacyEditor = useCallback((type: ClinicalDocumentUiType) => {
    setEditingDocument(null);
    setEditorType(type);
    setIsEditorOpen(true);
  }, []);

  const fetchDocuments = useCallback(async () => {
    if (!appointmentId) {
      setDocuments([]);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const res = await getDocumentsByAppointment(appointmentId);
      if (res.success && Array.isArray(res.data)) {
        setDocuments(res.data);
      } else {
        setDocuments([]);
        toast.error(res.error || 'Erro ao buscar documentos');
      }
    } catch (err) {
      console.error(err);
      setDocuments([]);
      toast.error('Erro ao buscar documentos');
    } finally {
      setIsLoading(false);
    }
  }, [appointmentId]);

  const persistMemedResult = useCallback(
    async (result: MemedPrescriptionResult) => {
      if (!result.providerDocumentId) {
        pendingPersistRef.current = result;
        setPersistState('error');
        setPersistError(
          'Não foi possível obter o ID do documento. Se faltar assinatura (403), assine na Memed com Bird ID / VIDAS / A3 cloud. O .pfx do Dr House não assina a receita. Se o documento existir na Memed, vincule pelo ID.',
        );
        setShowMemedImport(true);
        toast.error(
          'ID do documento ausente. Use “Vincular” com o ID da Memed ou assine de novo na Memed (Bird ID).',
        );
        return;
      }

      if (persistInFlightRef.current) return;
      persistInFlightRef.current = true;
      pendingPersistRef.current = result;
      setPersistState('saving');
      setPersistError(null);

      try {
        const res = await confirmMemedDocument({
          providerDocumentId: result.providerDocumentId,
          appointmentId,
          digitalLinkHint: result.digitalLink,
          statusHint: result.status,
          renewedFromDocumentId: renewalSourceDocumentIdRef.current || undefined,
          kind: pendingKindRef.current,
        });

        if (!res.success || !res.data) {
          setPersistState('error');
          setPersistError(
            res.error ||
              (res.code === 'MEMED_UNAUTHORIZED' || res.status === 403
                ? 'Assine na Memed com certificado em nuvem (Bird ID, VIDAS ou A3 cloud). O cadastro de .pfx no App Saúde não assina a receita. O documento não foi gravado.'
                : res.status === 400
                  ? 'CPF/CRM da Memed não batem com o perfil. Confira a identidade Memed.'
                : 'O documento foi emitido na Memed, mas falhou o registro no App Saúde.'),
          );
          toast.error(
            res.error ||
              (res.status === 403
                ? 'Assine na Memed (Bird ID). Nada foi gravado como sucesso no App Saúde.'
                : res.status === 400
                  ? 'CPF/CRM da Memed não batem com o perfil.'
                : 'Documento emitido na Memed, mas não registrado no App Saúde. Você pode tentar novamente.'),
          );
          return;
        }

        setPersistState('success');
        pendingPersistRef.current = null;
        renewalSourceDocumentIdRef.current = null;
        setRenewalBanner(null);
        setRenewalItemsPreview([]);
        setShowMemedImport(false);
        setMemedImportId('');
        toast.success('Documento registrado no App Saúde.');
        await fetchDocuments();
        onDocumentGenerated?.(
          res.data.fileName || 'documento-memed.pdf',
          pendingKindRef.current === 'certificate'
            ? 'CERTIFICATE'
            : pendingKindRef.current === 'exam'
              ? 'EXAM'
              : 'PRESCRIPTION',
          res.data.id,
        );
      } catch (err) {
        console.error(err);
        setPersistState('error');
        setPersistError(
          'Falha de rede ao registrar o documento. A emissão Memed não será refeita — tente novamente.',
        );
        toast.error(
          'Receita emitida na Memed, mas o registro local falhou. Use “Tentar novamente”.',
        );
      } finally {
        persistInFlightRef.current = false;
      }
    },
    [appointmentId, fetchDocuments, onDocumentGenerated],
  );

  const {
    isLoading: isMemedLoading,
    openDigitalDocument,
    openRenewalPrescription,
  } = useMemedPrescription({
    patientId,
    onIssued: (result) => {
      // Emitido na Memed ≠ registrado no Dr. House
      void persistMemedResult(result);
    },
    onLegacyFallback: () => {
      openLegacyEditor(pendingLegacyTypeRef.current);
    },
  });

  useEffect(() => {
    if (appointmentId) fetchDocuments();
  }, [appointmentId, fetchDocuments]);

  const handleCreateDocument = async (type: ClinicalDocumentUiType) => {
    const usesMemedWidget =
      MEMED_PRESCRIPTION_TYPES.includes(type) ||
      MEMED_CERTIFICATE_EXAM_TYPES.includes(type);

    if (!usesMemedWidget) {
      openLegacyEditor(type);
      return;
    }

    pendingLegacyTypeRef.current = type;
    pendingKindRef.current = memedKindFromClinicalUi(type);
    renewalSourceDocumentIdRef.current = null;
    setRenewalBanner(null);
    setRenewalItemsPreview([]);
    setPersistState('idle');
    setPersistError(null);
    await openDigitalDocument(pendingKindRef.current);
  };

  const formatRenewalBanner = (base: PrescriptionRenewalBase): string => {
    const dateLabel = base.sourceIssuedAt
      ? format(new Date(base.sourceIssuedAt), 'dd/MM/yyyy', { locale: ptBR })
      : null;
    return dateLabel
      ? `Renovando receita de ${dateLabel}. Revise antes de emitir.`
      : 'Renovando receita anterior. Revise antes de emitir.';
  };

  const handleRenew = async (doc: DocumentDTO) => {
    if (renewLoadingId || isMemedLoading || persistState === 'saving') return;
    setRenewLoadingId(doc.id);
    setPersistState('idle');
    setPersistError(null);
    pendingKindRef.current = 'prescription';

    try {
      const res = await getDocumentRenewalBase(doc.id);
      if (!res.success || !res.data) {
        toast.error(
          res.error ||
            (res.code === 'PRESCRIPTION_NOT_RENEWABLE'
              ? 'Esta receita não está disponível para renovação.'
              : 'Não foi possível preparar a renovação.'),
        );
        return;
      }

      const base = res.data;
      renewalSourceDocumentIdRef.current = base.sourceDocumentId;
      setRenewalBanner(formatRenewalBanner(base));
      setRenewalItemsPreview(
        (base.prescription?.items || [])
          .map((item) => [item.name, item.dosage].filter(Boolean).join(' — '))
          .filter(Boolean)
          .slice(0, 8),
      );

      if (base.message) {
        toast.message(base.message);
      }

      const opened = await openRenewalPrescription(base);
      if (!opened.opened) {
        renewalSourceDocumentIdRef.current = null;
        setRenewalBanner(null);
        setRenewalItemsPreview([]);
      }
    } catch (err) {
      console.error(err);
      renewalSourceDocumentIdRef.current = null;
      setRenewalBanner(null);
      setRenewalItemsPreview([]);
      toast.error('Erro ao iniciar renovação da receita.');
    } finally {
      setRenewLoadingId(null);
    }
  };

  useEffect(() => {
    if (!autoRenewDocumentId || autoRenewDoneRef.current || isLoading) return;
    const doc = documents.find((d) => d.id === autoRenewDocumentId);
    if (!doc) return;
    autoRenewDoneRef.current = true;
    if (!isMemedPrescriptionRenewable(doc)) {
      toast.error('Esta receita não está disponível para renovação Memed.');
      return;
    }
    void handleRenew(doc);
  }, [autoRenewDocumentId, documents, isLoading]);

  const handleRetryPersist = async () => {
    const pending = pendingPersistRef.current;
    if (pending?.providerDocumentId) {
      await persistMemedResult(pending);
      return;
    }
    // Sem ID: “Tentar novamente” abre o vínculo manual (antes o botão não fazia nada).
    setShowMemedImport(true);
    setPersistError(
      'Informe o ID da receita na Memed para registrar no App Saúde, ou assine de novo na Memed (Bird ID).',
    );
    toast.message('Cole o ID da receita Memed abaixo para vincular.');
  };

  const handleImportMemedById = async () => {
    const providerDocumentId = memedImportId.trim();
    if (!providerDocumentId) {
      toast.error('Informe o ID da receita na Memed.');
      return;
    }
    setShowMemedImport(false);
    await persistMemedResult({
      providerDocumentId,
      status: 'issued',
      rawEventName: 'manual_import',
    });
    setMemedImportId('');
  };

  const handleView = async (docId: string) => {
    setActionLoadingId(docId);
    setViewingAccessUrl(null);
    try {
      const accessRes = await getDocumentAccess(docId);
      if (!accessRes.success || !accessRes.data) {
        toast.error(accessRes.error || 'Não foi possível abrir o documento');
        return;
      }

      const access = accessRes.data;
      if (!access.canOpen) {
        toast.error('Documento indisponível para visualização');
        return;
      }

      if (access.accessMode === 'DIGITAL_LINK' && access.accessUrl) {
        window.open(access.accessUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      const res = await getDocumentById(docId);
      if (res.success && res.data) {
        setViewingDocument(res.data);
        setViewingAccessUrl(null);
      } else {
        toast.error(res.error || 'Não foi possível abrir o documento');
      }
    } catch (err) {
      console.error(err);
      toast.error('Erro ao abrir documento');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleEdit = async (doc: DocumentDTO) => {
    if (doc.provider === 'MEMED') {
      toast.message('Receitas digitais são abertas pelo link seguro. Edição local não se aplica.');
      await handleView(doc.id);
      return;
    }
    setActionLoadingId(doc.id);
    try {
      const res = await getDocumentById(doc.id);
      if (!res.success || !res.data) {
        toast.error(res.error || 'Documento não encontrado para edição');
        return;
      }
      const type = String(res.data.documentType || '').toLowerCase();
      const uiType: ClinicalDocumentUiType =
        type === 'certificate'
          ? 'CERTIFICATE'
          : type === 'recipe'
            ? 'RECIPE'
            : type === 'report'
              ? 'EXAM'
              : 'PRESCRIPTION';
      setEditingDocument(res.data);
      setEditorType(uiType);
      setIsEditorOpen(true);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar documento para edição');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleExportPdf = async (docId: string) => {
    setActionLoadingId(docId);
    try {
      const accessRes = await getDocumentAccess(docId);
      if (
        accessRes.success &&
        accessRes.data?.accessMode === 'DIGITAL_LINK' &&
        accessRes.data.accessUrl
      ) {
        window.open(accessRes.data.accessUrl, '_blank', 'noopener,noreferrer');
        toast.success('Abrindo receita digital');
        return;
      }

      const { blob, fileName } = await downloadDocumentPdfFile(docId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success('PDF baixado com sucesso!');
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Falha ao exportar PDF');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleSendEmail = async (docId: string) => {
    setActionLoadingId(docId);
    try {
      const res = await sendDocumentEmail(docId);
      if (res.success) {
        toast.success(res.data?.message || 'Documento enviado por e-mail');
      } else {
        toast.error(res.error || 'Erro ao enviar e-mail');
      }
    } catch (err) {
      console.error(err);
      toast.error('Erro de conexão ao enviar e-mail');
    } finally {
      setActionLoadingId(null);
    }
  };

  const isBusy = isMemedLoading || persistState === 'saving' || Boolean(renewLoadingId);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white dark:bg-neutral-900 border rounded-xl shadow-sm">
      <div className="p-4 border-b flex justify-between items-center bg-neutral-50 dark:bg-neutral-900">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <FileText className="w-5 h-5 text-teal-600" />
          Documentos Clínicos
        </h3>
        <Button variant="ghost" size="icon" onClick={fetchDocuments} title="Atualizar">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {renewalBanner && (
        <div className="px-4 py-2 text-sm bg-teal-50 text-teal-900 border-b border-teal-100 space-y-1">
          <p className="font-medium">{renewalBanner}</p>
          {renewalItemsPreview.length > 0 && (
            <ul className="list-disc pl-5 text-xs text-teal-800/90">
              {renewalItemsPreview.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
          <p className="text-xs">
            Os medicamentos listados são referência. Preencha/revise no widget Memed antes de
            emitir.
          </p>
        </div>
      )}
      {persistState === 'saving' && (
        <div className="px-4 py-2 text-sm bg-amber-50 text-amber-900 border-b border-amber-100">
          Registrando documento no App Saúde…
        </div>
      )}
      {persistState === 'error' && (
        <div className="px-4 py-2 text-sm bg-red-50 text-red-800 border-b border-red-100 flex flex-wrap items-center gap-2 justify-between">
          <span>{persistError || 'Falha ao registrar o documento localmente.'}</span>
          <Button size="sm" variant="outline" onClick={handleRetryPersist} disabled={isBusy}>
            Tentar novamente
          </Button>
        </div>
      )}

      {showMemedImport && (
        <div className="px-4 py-3 text-sm bg-slate-50 border-b flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-1">
            <label htmlFor="memed-import-id" className="text-xs font-medium text-slate-700">
              ID da receita na Memed (já emitida)
            </label>
            <input
              id="memed-import-id"
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
              placeholder="Ex.: 1234567"
              value={memedImportId}
              onChange={(e) => setMemedImportId(e.target.value)}
              disabled={isBusy}
            />
            <p className="text-xs text-slate-500">
              Use quando a receita aparece na Memed, mas ainda não no App Saúde.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setShowMemedImport(false);
                setMemedImportId('');
              }}
              disabled={isBusy}
            >
              Cancelar
            </Button>
            <Button size="sm" onClick={() => void handleImportMemedById()} disabled={isBusy}>
              Vincular
            </Button>
          </div>
        </div>
      )}

      <div className="p-4 grid grid-cols-2 lg:grid-cols-4 gap-3 border-b">
        <Button
          onClick={() => handleCreateDocument('PRESCRIPTION')}
          className="bg-blue-600 hover:bg-blue-700"
          size="sm"
          disabled={isBusy}
        >
          {isBusy ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Plus className="w-4 h-4 mr-2" />
          )}
          Prescrição
        </Button>
        <Button
          onClick={() => handleCreateDocument('RECIPE')}
          className="bg-teal-600 hover:bg-teal-700"
          size="sm"
          disabled={isBusy}
        >
          <Plus className="w-4 h-4 mr-2" /> Receituário
        </Button>
        <Button onClick={() => handleCreateDocument('CERTIFICATE')} className="bg-green-600 hover:bg-green-700" size="sm" disabled={isBusy}>
          <Plus className="w-4 h-4 mr-2" /> Atestado
        </Button>
        <Button onClick={() => handleCreateDocument('EXAM')} className="bg-amber-600 hover:bg-amber-700" size="sm" disabled={isBusy}>
          <Plus className="w-4 h-4 mr-2" /> Pedido Exame
        </Button>
      </div>
      <div className="px-4 pb-3 border-b">
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs text-slate-600"
          onClick={() => setShowMemedImport((v) => !v)}
          disabled={isBusy}
        >
          Vincular receita já emitida na Memed
        </Button>
      </div>

      <div className="flex-1 overflow-auto p-4 bg-neutral-50/50 dark:bg-neutral-900/50">
        <h4 className="font-semibold mb-4 text-sm text-muted-foreground uppercase tracking-wider">
          Documentos desta consulta
        </h4>

        {isLoading ? (
          <div className="text-center p-8 text-muted-foreground">Carregando documentos...</div>
        ) : documents.length === 0 ? (
          <div className="text-center p-12 bg-white dark:bg-neutral-800 rounded-lg border border-dashed">
            <File className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
            <p className="text-muted-foreground">Nenhum documento criado nesta consulta.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {documents.map((doc) => {
              const statusLabel = documentStatusLabel(doc);
              return (
                <Card key={doc.id} className="overflow-hidden">
                  <CardContent className="p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 rounded-lg shrink-0">
                        <FileText className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">
                          {clinicalDocumentLabel(doc.documentType)}
                          {statusLabel ? (
                            <span className="ml-2 text-xs font-normal text-muted-foreground">
                              · {statusLabel}
                            </span>
                          ) : null}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {doc.createdAt
                            ? format(new Date(doc.createdAt), "dd/MM/yyyy 'às' HH:mm", {
                                locale: ptBR,
                              })
                            : '—'}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={actionLoadingId === doc.id}
                        onClick={() => handleView(doc.id)}
                      >
                        <Eye className="w-4 h-4 mr-1" /> Abrir
                      </Button>
                      {isMemedPrescriptionRenewable(doc) && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isBusy || renewLoadingId === doc.id}
                          onClick={() => handleRenew(doc)}
                        >
                          {renewLoadingId === doc.id ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <RotateCcw className="w-4 h-4 mr-1" />
                          )}
                          Renovar Receita
                        </Button>
                      )}
                      {doc.provider !== 'MEMED' && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={actionLoadingId === doc.id}
                          onClick={() => handleEdit(doc)}
                        >
                          <Pencil className="w-4 h-4 mr-1" /> Editar
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={actionLoadingId === doc.id}
                        onClick={() => handleExportPdf(doc.id)}
                      >
                        <Printer className="w-4 h-4 mr-1" /> PDF
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={actionLoadingId === doc.id}
                        onClick={() => handleSendEmail(doc.id)}
                      >
                        <Send className="w-4 h-4 mr-1" /> E-mail
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {isEditorOpen && (
        <DocumentEditor
          appointmentId={appointmentId}
          patientId={patientId}
          documentType={editorType}
          existingDocument={editingDocument}
          onClose={() => {
            setIsEditorOpen(false);
            setEditingDocument(null);
          }}
          onSuccess={(savedId) => {
            setIsEditorOpen(false);
            setEditingDocument(null);
            fetchDocuments();
            if (onDocumentGenerated && !editingDocument) {
              onDocumentGenerated('novo-documento.pdf', editorType, savedId);
            }
          }}
        />
      )}

      {viewingDocument && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <Card className="w-full max-w-3xl max-h-[85vh] flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">
                {clinicalDocumentLabel(viewingDocument.documentType)}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setViewingDocument(null)}>
                Fechar
              </Button>
            </div>
            <div className="p-6 overflow-auto">
              <SafeContent
                content={viewingDocument.content || ''}
                showSkeleton={false}
                className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap"
              />
              {viewingAccessUrl ? (
                <div className="mt-4">
                  <a
                    href={viewingAccessUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700"
                  >
                    Abrir receita digital
                  </a>
                </div>
              ) : null}
            </div>
            <div className="p-4 border-t flex gap-2 justify-end">
              {viewingDocument.provider !== 'MEMED' && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setViewingDocument(null);
                    handleEdit(viewingDocument);
                  }}
                >
                  Editar
                </Button>
              )}
              <Button
                onClick={() => {
                  handleExportPdf(viewingDocument.id);
                }}
              >
                PDF
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
